import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconButton,
} from "@prismicio/editor-ui"
import { useState } from "react"

import { formatSnippetArgs } from "../../../../utils"
import { useAPIExplorer } from "../../../APIExplorerContext"
import { RequestFormInputBase } from "../RequestFormInputBase"
import { FilterConfigDialog } from "./FilterConfigDialog"
import { filters, type FilterType } from "./types"

/**
 * Display a fieldset managing a request filters setting.
 */
export function RequestFiltersFieldset() {
  const { request } = useAPIExplorer()

  return request.advanced ? <RequestFiltersFieldsetBase /> : null
}

function RequestFiltersFieldsetBase() {
  const { request } = useAPIExplorer()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedType, setSelectedType] = useState<FilterType>("at")
  const [editIndex, setEditIndex] = useState<number | undefined>()

  function addFilter(type: FilterType) {
    setSelectedType(type)
    setEditIndex(undefined)
    setDialogOpen(true)
  }

  function editFilter(index: number) {
    const maybeFilter = request.filters[index]
    if (!maybeFilter) return

    setSelectedType(maybeFilter.type)
    setEditIndex(index)
    setDialogOpen(true)
  }

  function removeFilter(index: number) {
    request.setFilters([
      ...request.filters.slice(0, index),
      ...request.filters.slice(index + 1),
    ])
  }

  return (
    <RequestFormInputBase label="FILTERS">
      <Box display="grid" gap={4}>
        <ActiveFilters editFilter={editFilter} removeFilter={removeFilter} />
        <AddFilterButton onSelect={addFilter} />
        <FilterConfigDialog
          key={editIndex ?? `add-${selectedType}`}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          type={selectedType}
          editIndex={editIndex}
        />
      </Box>
    </RequestFormInputBase>
  )
}

type ActiveFiltersProps = {
  editFilter: (index: number) => void
  removeFilter: (index: number) => void
}

function ActiveFilters(props: ActiveFiltersProps) {
  const { editFilter, removeFilter } = props
  const { request } = useAPIExplorer()

  if (!request.filters.length) return null

  return (
    <ActionList variant="compact-bordered" sx={{ overflowY: "hidden" }}>
      {request.filters.map((filter, index) => (
        <ActionListItem
          key={`${index}-${JSON.stringify(filter)}`}
          onClick={() => editFilter(index)}
          endOverlay={
            <IconButton
              icon="delete"
              variant="ghost"
              size="small"
              onClick={() => removeFilter(index)}
            />
          }
          oneLine
          noWrapTitle
        >
          {filter.type}({formatSnippetArgs(filter.args)})
        </ActionListItem>
      ))}
    </ActionList>
  )
}

interface AddFilterButtonProps {
  onSelect: (type: FilterType) => void
}

function AddFilterButton(props: AddFilterButtonProps) {
  const { onSelect } = props

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button startIcon="add" color="grey">
          Add
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" minWidth="full-trigger-width">
        {filters.map(({ type, icon }) => (
          <DropdownMenuItem
            key={type}
            startIcon={<Icon name={icon} />}
            onSelect={() => onSelect(type)}
          >
            {type}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
